import React, { useState } from 'react';

const ContactForm = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Something went wrong. Please try again.');
            }

            setSubmitSuccess(true);
            setFormData({
                name: '',
                email: '',
                subject: '',
                message: ''
            });
        } catch (error) {
            setSubmitError(error.message || 'There was an error sending your message. Please try again.');
            console.error('Contact form error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Separator line */}
            <div className="w-full" style={{
                borderTop: '1px solid',
                borderImageSource: 'linear-gradient(90deg, #000000 0%, #7A7A7F 52.4%, #000000 100%)',
                borderImageSlice: '1',
            }}></div>

            <div className="w-full bg-black text-white py-20">
                <div className="max-w-7xl mx-auto px-8 md:px-16">
                    <h2 className="text-[30px] md:text-[55px] leading-tight font-light mb-8">Get In Touch</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        <div>
                            <p className="mb-4">Have a question, a custom request, or just want to say hi?</p>
                            <p className="mb-8">We'd love to hear from you.</p>

                            <p className="mb-4">
                                Fill out the form below and we'll get back to you as soon as we can —
                                usually within 1–2 business days. Whether you're wondering about your
                                order, star map details, or anything in between, we're here to help.
                            </p>
                        </div>

                        <div>
                            {submitSuccess ? (
                                <div className="bg-green-800 bg-opacity-20 border border-green-500 rounded-md p-6 text-center">
                                    <h3 className="text-xl mb-2">Message Sent!</h3>
                                    <p>Thank you for contacting us. We'll be in touch soon.</p>
                                    <button
                                        onClick={() => setSubmitSuccess(false)}
                                        className="mt-4 bg-[#A961ED] text-white px-6 py-3 rounded-full hover:bg-purple-700 transition-colors"
                                    >
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Name Surname"
                                                required
                                                className="w-full p-3 bg-transparent border-0 border-b border-gray-500 focus:border-purple-500 focus:outline-none"
                                            />
                                        </div>

                                        <div>
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="Email Address"
                                                required
                                                className="w-full p-3 bg-transparent border-0 border-b border-gray-500 focus:border-purple-500 focus:outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <input
                                            type="text"
                                            name="subject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            placeholder="Subject"
                                            required
                                            className="w-full p-3 bg-transparent border-0 border-b border-gray-500 focus:border-purple-500 focus:outline-none"
                                        />
                                    </div>

                                    <div>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Message"
                                            required
                                            rows="5"
                                            className="w-full p-3 bg-transparent border border-gray-500 focus:border-purple-500 focus:outline-none resize-none"
                                        />
                                    </div>

                                    {submitError && (
                                        <div className="bg-red-800 bg-opacity-20 border border-red-500 rounded-md p-4 text-red-200">
                                            <p className="flex items-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                                </svg>
                                                {submitError}
                                            </p>
                                        </div>
                                    )}

                                    <div>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full md:w-auto bg-[#A961ED] text-white px-8 py-4 rounded-full hover:bg-purple-700 transition-colors disabled:opacity-70 flex items-center justify-center"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Sending...
                                                </>
                                            ) : 'Send Message'}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContactForm; 